import React from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface StatsCardProps {
  title: string
  value: string | number
  change?: number
  changeType?: 'increase' | 'decrease' | 'neutral'
  icon?: React.ReactNode
  className?: string
  description?: string
  iconContainerClassName?: string
  footerLabel?: string
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  changeType = 'neutral',
  icon,
  className,
  description,
  iconContainerClassName,
  footerLabel,
}) => {
  const getTrendIcon = () => {
    if (changeType === 'increase') {
      return <TrendingUp className="h-4 w-4 text-green-600" />
    } else if (changeType === 'decrease') {
      return <TrendingDown className="h-4 w-4 text-red-600" />
    }
    return <Minus className="h-4 w-4 text-gray-600" />
  }

  const getTrendColor = () => {
    if (changeType === 'increase') {
      return 'text-green-600'
    } else if (changeType === 'decrease') {
      return 'text-red-600'
    }
    return 'text-gray-600'
  }

  return (
    <Card className={cn('rounded-3xl border-slate-200/80 bg-white/95 shadow-sm', className)}>
      <CardHeader className="space-y-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {title}
            </CardTitle>
            {description && <p className="text-sm text-slate-500">{description}</p>}
          </div>
          {icon && (
            <div
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 shadow-sm',
                iconContainerClassName
              )}
            >
              {icon}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-3xl font-semibold tracking-tight text-slate-950">{value}</div>
        {change !== undefined && (
          <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs">
            {getTrendIcon()}
            <p className={getTrendColor()}>
              {change > 0 ? '+' : ''}
              {change}% {footerLabel ?? 'vs previous period'}
            </p>
          </div>
        )}
        {change === undefined && footerLabel && (
          <p className="text-xs text-slate-500">{footerLabel}</p>
        )}
      </CardContent>
    </Card>
  )
}
